import { comboboxKey, createStatusDot, initGameFormCombobox } from './game-form-combobox.js';

/**
 * Status stays a native `<select>` in the submitted form (and for no-JS); this module only swaps
 * the visible control for the shared combobox so the status can carry its color dot. The select
 * remains the source of truth: every pick writes it and fires `input`/`change` for validation.
 */
(function () {
    const field = document.querySelector('[data-game-form-status-field]');
    const select = field?.querySelector('select[name="status"]');
    const combobox = field?.querySelector('[data-game-form-status-combobox]');
    const input = field?.querySelector('[data-game-form-status-input]');
    const toggle = field?.querySelector('[data-game-form-status-toggle]');
    const list = field?.querySelector('[data-game-form-status-options]');
    const dotSlot = field?.querySelector('[data-game-form-status-dot]');

    if (
        !(field instanceof HTMLElement)
        || !(select instanceof HTMLSelectElement)
        || !(combobox instanceof HTMLElement)
        || !(input instanceof HTMLInputElement)
        || !(toggle instanceof HTMLButtonElement)
        || !(list instanceof HTMLUListElement)
        || !(dotSlot instanceof HTMLElement)
    ) {
        return;
    }

    const statuses = Array.from(select.options)
        .filter((option) => option.value !== '')
        .map((option) => ({
            type: 'option',
            value: option.value,
            label: option.textContent.trim(),
            tone: option.dataset.statusTone ?? null,
        }));

    const showSelected = () => {
        const selected = statuses.find((status) => status.value === select.value);
        input.value = selected?.label ?? '';
        dotSlot.replaceChildren(createStatusDot(selected?.tone ?? null));
    };

    initGameFormCombobox({
        root: field,
        input,
        toggle,
        list,
        buildOptions: (query) => {
            const queryKey = comboboxKey(query);
            const matches = statuses.filter((status) => comboboxKey(status.label).includes(queryKey));

            return matches.length > 0 ? matches : [{ type: 'empty', value: '', label: 'No matching statuses.' }];
        },
        onSelect: (option) => {
            select.value = option.value;
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
            showSelected();
        },
        // Typing is only a filter; leaving without a pick shows the current status again.
        onClose: showSelected,
        isCurrent: (option) => option.value === select.value,
        filterOnly: true,
    });

    // A server-rendered status error moves to the visible control together with the select's role.
    input.setAttribute('aria-invalid', select.getAttribute('aria-invalid') ?? 'false');
    select.hidden = true;
    combobox.hidden = false;
    showSelected();
})();
